import { useState, useRef, useEffect, ReactNode } from "react";
import { CubeIcon } from "@heroicons/react/24/outline";
import { MenuAccountIcon as UserCircleIcon } from "./atoms/Icon";
import logoAutorace from "../assets/images/logo_autorace.png";
import circuitPlannerLogoWhite from "../assets/images/circuit_planner_logo_white.png";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { Dialog, DialogHeader, DialogBody, Button } from "./atoms";
import { PasswordChangeForm } from "../features/auth/ui/PasswordChangeForm";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { setStoreSelection, clearStoreSelection, setMenus, setHasUnread, type RootState } from "../app/store";
import { fetchMenus, type MenuResponse } from "../api/menus";
import { fetchUnreadBadges } from "../api/notification";
import { fetchStores } from "../api/stores";
import { fetchOrganizations } from "../api/organizations";
import { fetchStoreDepartments } from "../api/departments";
import { useSidebarMenus } from "../shared/menu/useMenus";
import type { MenuItem } from "../shared/menu/menuTypes";

// メニュー localStorage キャッシュ
const MENU_CACHE_KEY = "app_menus";
const MENU_CACHE_TTL = 60 * 60 * 1000; // 1時間

function getMenuCache(): MenuResponse[] {
    try {
        const cached = localStorage.getItem(MENU_CACHE_KEY);
        if (!cached) return [];
        const { data, timestamp } = JSON.parse(cached) as { data: MenuResponse[]; timestamp: number };
        if (Date.now() - timestamp > MENU_CACHE_TTL) return [];
        return Array.isArray(data) ? data : [];
    } catch {
        return [];
    }
}

function setMenuCache(data: MenuResponse[]): void {
    try {
        localStorage.setItem(MENU_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
    } catch {
        // ストレージ容量不足等は無視
    }
}

// 下部メニュー code: 特殊クリック動作を持つもの
const BOTTOM_CODE_NOTIFICATIONS = "notifications";
const BOTTOM_CODE_LOGIN_INFO = "login_info";

const LOGIN_CODE_CHANGE_PASSWORD = "change_password";
const LOGIN_CODE_CHANGE_EMAIL = "change_email";
const LOGIN_CODE_LOGOUT = "logout";

interface LayoutProps {
    children: ReactNode;
    /** サイドバーの開閉モード
     *  - "collapsible": トグルボタンで開閉可能（デフォルト）
     *  - "always-expanded": 常に展開状態（トグルボタン非表示）
     */
    sidebarMode?: "collapsible" | "always-expanded";
}

const Layout = ({ children, sidebarMode = "collapsible" }: LayoutProps) => {
    const isAlwaysExpanded = sidebarMode === "always-expanded";
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation("common");
    const { isDesktop } = useBreakpoint();
    const [isSideMenuExpanded, setIsSideMenuExpanded] = useState(isAlwaysExpanded);
    const [isLoginInfoPopoverOpen, setIsLoginInfoPopoverOpen] = useState(false);
    const [loginInfoPopoverPos, setLoginInfoPopoverPos] = useState<{ left: number; bottom: number } | null>(null);
    const [showNotImplementedModal, setShowNotImplementedModal] = useState<string | null>(null);
    const [isPasswordChangeOpen, setIsPasswordChangeOpen] = useState(false);
    const [isPasswordChangeComplete, setIsPasswordChangeComplete] = useState(false);
    const loginInfoButtonRef = useRef<HTMLButtonElement>(null);
    const dispatch = useDispatch();
    const storeSelectionState = useSelector((state: RootState) => state.storeSelection);
    const hasUnreadNotification = useSelector((state: RootState) => state.notification.hasUnread);


    // ログイン後、店舗選択が未設定の場合にスタッフの所属から自動セット
    useEffect(() => {
        if (storeSelectionState.store || !user || !user.storeId) return;

        let cancelled = false;

        const resolveInitialStore = async () => {
            try {
                const [storesRes, orgsRes, deptsRes] = await Promise.all([
                    fetchStores(),
                    fetchOrganizations(),
                    fetchStoreDepartments(user.storeId),
                ]);
                if (cancelled) return;

                const store = storesRes.items.find(s => s.id === user.storeId);
                if (!store) return;

                // store.organization_id → parent_organization_id チェーンで上位を解決
                // orgLevels の key は organization.level（StoreSwitchPopup と統一）
                const orgs = orgsRes.items;
                const findOrg = (id: number) => orgs.find(u => u.id === id) ?? null;

                const orgLevels: Record<number, { id: number; name: string }> = {};
                let currentOrg = store.organization_id ? findOrg(store.organization_id) : null;
                while (currentOrg) {
                    // level=11 (店舗) と level=12 (部門) は store/department フィールドへ送るので
                    // orgLevels には level<=9 と level=13 のみを格納する
                    if (currentOrg.level <= 9 || currentOrg.level === 13) {
                        orgLevels[currentOrg.level] = { id: currentOrg.id, name: currentOrg.name ?? "" };
                    }
                    currentOrg = currentOrg.parent_organization_id ? findOrg(currentOrg.parent_organization_id) : null;
                }

                // 部門: スタッフの所属部門を優先、なければ先頭
                const dept = deptsRes.items.find(d => d.id === user.departmentId) ?? deptsRes.items[0] ?? null;

                if (Object.keys(orgLevels).length > 0 && dept) {
                    dispatch(setStoreSelection({
                        orgLevels,
                        store: {
                            id: store.id,
                            organizationId: store.organization_id ?? undefined,
                            name: store.name,
                        },
                        department: { id: dept.id, organizationId: dept.organization_id, name: dept.name },
                    }));
                }
            } catch (error) {
                if (cancelled) return;
                console.warn("Failed to auto-resolve store selection:", error);
            }
        };

        resolveInitialStore();
        return () => { cancelled = true; };
    }, [storeSelectionState.store, user, dispatch]);

    // タブレット幅になったらサイドバーを自動で折りたたむ（always-expanded では無効）
    useEffect(() => {
        if (isAlwaysExpanded) return;
        if (!isDesktop && isSideMenuExpanded) {
            setIsSideMenuExpanded(false);
        }
    }, [isDesktop, isAlwaysExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLogout = () => {
        dispatch(clearStoreSelection());
        // logout() 内でナビゲーション + キャッシュクリアも行うため、
        // ここでは navigate() を呼ばない
        logout();
    };

    const handleNotImplemented = (feature: string) => {
        setShowNotImplementedModal(feature);
        setIsLoginInfoPopoverOpen(false);
    };

    const handleSideMenuToggle = () => {
        if (isAlwaysExpanded) return;
        setIsSideMenuExpanded((prev) => !prev);
    };

    // メニュー取得（キャッシュファースト: localStorage → API バックグラウンド更新）
    // 1回で全件取得（上部・下部・タブ・ドロップダウン）→ フロントで type ごとに振り分け
    const [dbMenus, setDbMenus] = useState<MenuResponse[]>(getMenuCache);
    useEffect(() => {
        const controller = new AbortController();
        if (dbMenus.length > 0) dispatch(setMenus(dbMenus));
        fetchMenus({ platform: "web" }, controller.signal)
            .then((res) => {
                setDbMenus(res.items);
                setMenuCache(res.items);
                dispatch(setMenus(res.items));
            })
            .catch(() => {}); // キャッシュがあるのでエラー時は何もしない
        return () => { controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // サイドメニュー未読バッジ: マウント時に未読状態を取得（軽量API 1本）
    const employeeId = user?.employeeId ?? 0;
    const tenantId = user?.tenantId ?? "";
    useEffect(() => {
        if (!employeeId || !tenantId) return;
        const controller = new AbortController();

        fetchUnreadBadges({ tenantId, employeeId }, controller.signal)
            .then((res) => {
                if (!controller.signal.aborted) {
                    dispatch(setHasUnread(res.message || res.topicha));
                }
            })
            .catch(() => {});

        return () => { controller.abort(); };
    }, [employeeId, dispatch]);

    // サイドバー構造（上部・下部 × 親子）を取得
    const sidebarMenus = useSidebarMenus();
    const location = useLocation();

    /** 現在のURLがメニュー項目にマッチするか判定（自身または子タブのURLに前方一致） */
    const isMenuActive = (item: MenuItem): boolean => {
        const pathname = location.pathname;
        if (item.url && pathname.startsWith(item.url)) return true;
        return item.children.some((child) => child.url && pathname.startsWith(child.url));
    };

    /**
     * 下部メニュー項目クリック時の特殊動作
     * 戻り値 true: 動作処理済み（Link 遷移しない）
     *        false: 通常動作に委譲
     */
    const handleBottomItemClick = (item: MenuItem): boolean => {
        if (item.code === BOTTOM_CODE_LOGIN_INFO) {
            // ボタンの右横にポップオーバー表示
            if (isLoginInfoPopoverOpen) {
                setIsLoginInfoPopoverOpen(false);
                return true;
            }
            const rect = loginInfoButtonRef.current?.getBoundingClientRect();
            if (rect) {
                setLoginInfoPopoverPos({
                    left: rect.right + 4,
                    bottom: window.innerHeight - rect.bottom,
                });
                setIsLoginInfoPopoverOpen(true);
            }
            return true;
        }
        // お知らせ等: 子項目の先頭URLへ遷移（ドロップダウンは廃止）
        if (item.children.length > 0) {
            const firstChild = item.children[0];
            if (firstChild?.url) {
                navigate(firstChild.url);
                return true;
            }
        }
        return false;
    };

    /**
     * ログイン情報ポップオーバー内の項目クリック時の特殊動作
     * 戻り値 true: 動作処理済み、false: 通常 Link 遷移
     */
    const handleLoginInfoChildClick = (child: MenuItem): boolean => {
        if (child.code === LOGIN_CODE_LOGOUT) {
            setIsLoginInfoPopoverOpen(false);
            handleLogout();
            return true;
        }
        if (child.code === LOGIN_CODE_CHANGE_PASSWORD) {
            setIsLoginInfoPopoverOpen(false);
            setIsPasswordChangeOpen(true);
            return true;
        }
        if (child.code === LOGIN_CODE_CHANGE_EMAIL) {
            handleNotImplemented(child.name);
            return true;
        }
        return false;
    };

    /** 上部メニュー1項目の描画 */
    const renderTopItem = (item: MenuItem) => {
        const active = isMenuActive(item);
        return (
            <Link
                key={item.id}
                to={item.url ?? "#"}
                className="block py-1 px-2 transition-colors"
            >
                <div
                    className={`flex items-center h-10 w-full rounded-lg pl-[10px] ${
                        active ? "bg-white text-brand-500" : ""
                    }`}
                >
                    <span className="w-5 h-5 flex-shrink-0 [&>svg]:w-full [&>svg]:h-full">{resolveIcon(item.icon)}</span>
                    <span className="text-sm font-bold whitespace-nowrap ml-3 overflow-hidden">{item.name}</span>
                </div>
            </Link>
        );
    };

    /** 下部メニュー1項目の描画（QRコード/お知らせ/店舗切替/ログイン情報） */
    const renderBottomItem = (item: MenuItem) => {
        const showUnreadBadge = item.code === BOTTOM_CODE_NOTIFICATIONS && hasUnreadNotification;
        const isLoginInfo = item.code === BOTTOM_CODE_LOGIN_INFO;
        const buttonRef = isLoginInfo ? loginInfoButtonRef : undefined;

        return (
            <button
                key={item.id}
                ref={buttonRef}
                onClick={() => {
                    if (!handleBottomItemClick(item)) {
                        if (item.url) navigate(item.url);
                    }
                }}
                className="block w-full py-1 px-2 hover:bg-brand-600 transition-colors"
            >
                <div
                    className={`flex items-center h-10 w-full rounded-lg pl-[10px] ${
                        isLoginInfo ? "bg-brand-400" : ""
                    }`}
                >
                    <span className="relative w-5 h-5 flex-shrink-0">
                        {resolveIcon(item.icon)}
                        {showUnreadBadge && (
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500" />
                        )}
                    </span>
                    <span className="text-sm font-bold whitespace-nowrap ml-3 overflow-hidden text-left">
                        {isLoginInfo ? (user?.username ?? item.name) : item.name}
                    </span>
                </div>
            </button>
        );
    };

    return (
        <div className="h-svh bg-[#E9F1F2] overflow-x-auto overflow-y-hidden">
            <div className="h-full flex min-w-[640px]">
            {/* 左サイドメニュー */}
            <aside
                className={`${
                    isSideMenuExpanded ? "w-56" : "w-14"
                } bg-brand-500 text-white flex flex-col flex-shrink-0 transition-[width] duration-300 ease-in-out overflow-hidden will-change-[width]`}
            >
                {/* ロゴ */}
                <Link
                    to="/home"
                    className="border-b border-brand-400 flex flex-col items-start justify-center py-3 px-3 gap-1"
                >
                    <img
                        src={logoAutorace}
                        alt="Auto Race"
                        className={`object-contain flex-shrink-0 transition-all duration-300 ${
                            isSideMenuExpanded ? "h-10" : "h-7"
                        }`}
                    />
                    <img
                        src={circuitPlannerLogoWhite}
                        alt="Circuit Planner"
                        className={`object-contain flex-shrink-0 transition-opacity duration-300 ${
                            isSideMenuExpanded ? "h-5 opacity-100" : "h-0 opacity-0"
                        }`}
                    />
                </Link>

                {/* 上部メニュー (sidebar_top) */}
                <nav className="flex-1 py-2 overflow-y-auto scrollbar-hide">
                    {sidebarMenus.top.map(renderTopItem)}
                </nav>

                {/* 下部メニュー (sidebar_bottom) */}
                <div className="border-t border-white">
                    {sidebarMenus.bottom.map(renderBottomItem)}
                </div>

                {/* 開閉ボタン（always-expanded では非表示） */}
                {!isAlwaysExpanded && (
                    <button
                        onClick={handleSideMenuToggle}
                        className="h-12 w-full flex items-center pl-[18px] hover:bg-brand-600 transition-colors"
                    >
                        <span className="w-5 h-5 flex-shrink-0 [&>svg]:w-full [&>svg]:h-full">
                            {resolveIcon(isSideMenuExpanded ? "side-menu-is-open.svg" : "side-menu-is-close.svg")}
                        </span>
                    </button>
                )}
            </aside>

            {/* メインコンテンツエリア */}
            <div className="flex-1 flex flex-col min-w-0 overflow-y-auto [&>*]:shrink-0">
                {children}
            </div>

            {/* ログイン情報ポップオーバー（ボタン右横に表示） */}
            {isLoginInfoPopoverOpen && loginInfoPopoverPos && (
                <>
                    {/* クリック外で閉じる用のオーバーレイ */}
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsLoginInfoPopoverOpen(false)}
                    />
                    <div
                        className="fixed z-50 w-48 bg-white rounded shadow-lg border border-gray-200 py-1"
                        style={{
                            left: `${loginInfoPopoverPos.left}px`,
                            bottom: `${loginInfoPopoverPos.bottom}px`,
                        }}
                    >
                        {sidebarMenus.bottom
                            .find((i) => i.code === BOTTOM_CODE_LOGIN_INFO)
                            ?.children.filter((child) => child.code !== LOGIN_CODE_CHANGE_EMAIL)
                            .map((child) => (
                                <button
                                    key={child.id}
                                    onClick={() => {
                                        if (!handleLoginInfoChildClick(child)) {
                                            if (child.url) {
                                                setIsLoginInfoPopoverOpen(false);
                                                navigate(child.url);
                                            }
                                        }
                                    }}
                                    className={`w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                                        child.code === LOGIN_CODE_LOGOUT ? "border-t border-gray-200" : ""
                                    }`}
                                >
                                    {child.name}
                                </button>
                            ))}
                    </div>
                </>
            )}

            {/* パスワード変更ダイアログ */}
            {isPasswordChangeOpen && (
                <Dialog className="w-full max-w-xl">
                    <DialogHeader title="パスワード変更" onClose={() => setIsPasswordChangeOpen(false)} />
                    <PasswordChangeForm onSuccess={() => {
                        setIsPasswordChangeOpen(false);
                        setIsPasswordChangeComplete(true);
                    }} />
                </Dialog>
            )}

            {/* パスワード変更完了ダイアログ */}
            {isPasswordChangeComplete && (
                <Dialog className="w-full max-w-sm">
                    <DialogHeader title="パスワードの変更完了" onClose={() => setIsPasswordChangeComplete(false)} className="border-b-0" />
                    <DialogBody>
                        <p className="text-sm text-gray-700">パスワードの変更が完了しました。</p>
                        <div className="flex justify-end mt-4">
                            <Button variant="primary" size="md" onClick={() => setIsPasswordChangeComplete(false)}>
                                閉じる
                            </Button>
                        </div>
                    </DialogBody>
                </Dialog>
            )}

            {/* 未実装モーダル */}
            {showNotImplementedModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
                        <p className="text-center text-gray-700 mb-4">
                            {t("NOT_IMPLEMENTED", { feature: showNotImplementedModal })}
                        </p>
                        <div className="flex justify-center">
                            <button
                                onClick={() => setShowNotImplementedModal(null)}
                                className="px-6 py-2 bg-brand-500 text-white rounded hover:bg-brand-600 transition-colors"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

/**
 * atoms/Icon フォルダ配下の *.svg を全件 raw で読み込み、
 * DB menu.icon のファイル名 → SVG 文字列 に解決できるようにする。
 * 新しいアイコンはファイルを配置するだけで反映される。
 */
const RAW_MENU_SVGS = import.meta.glob("./atoms/Icon/*.svg", {
    eager: true,
    query: "?raw",
    import: "default",
}) as Record<string, string>;

/** SVG 内のハードコードされた色を currentColor に正規化（Tailwind の text-* で着色可能にする） */
function normalizeSvgColors(svg: string): string {
    return svg
        .replace(/stroke="(?!none|currentColor|url\()[^"]*"/g, 'stroke="currentColor"')
        .replace(/fill="(?!none|currentColor|url\()[^"]*"/g, 'fill="currentColor"')
        .replace(/stroke:\s*(?!none|currentColor|url\()[^;"}\s]+/g, "stroke: currentColor")
        .replace(/fill:\s*(?!none|currentColor|url\()[^;"}\s]+/g, "fill: currentColor");
}

/** ファイル名 → 正規化済み SVG 文字列 */
const MENU_SVG_MAP: Record<string, string> = Object.fromEntries(
    Object.entries(RAW_MENU_SVGS).map(([filePath, raw]) => {
        const filename = filePath.split("/").pop() ?? "";
        return [filename, normalizeSvgColors(raw)];
    }),
);

/**
 * DB menu.icon の値からアイコンノードを解決する。
 * - null / 未登録ファイル → HeroIcon の CubeIcon をフォールバック表示
 * - 登録済みファイル → atoms/Icon/<name> の SVG をインライン描画
 */
function resolveIcon(iconName: string | null): ReactNode {
    if (!iconName) return <CubeIcon className="w-full h-full" />;
    const svg = MENU_SVG_MAP[iconName];
    if (!svg) return <CubeIcon className="w-full h-full" />;
    return (
        <span
            className="block w-full h-full [&>svg]:w-full [&>svg]:h-full"
            dangerouslySetInnerHTML={{ __html: svg }}
        />
    );
}

export default Layout;
